import { useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { fmtMoney, todayStr, buildShiftMaps, balanceClass } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PrintableStatement } from "@/components/PrintableStatement";
import { Plus, PencilSimple, Trash, FloppyDisk, Plant, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ShiftsWages() {
  const [shifts, setShifts] = useState([]);
  const [rates, setRates] = useState({ M: {}, F: {} });
  const [effectiveDate, setEffectiveDate] = useState(todayStr());
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", start: "", end: "" });
  const [editId, setEditId] = useState(null);

  const maps = buildShiftMaps(shifts);

  const loadShifts = () => api.get("/shifts").then((r) => setShifts(r.data));
  const loadRates = () => api.get("/wage-rates/effective").then((r) => setRates(r.data.rates));
  const loadHistory = () => api.get("/wage-rates/history").then((r) => setHistory(r.data));

  const loadAll = () => { loadShifts(); loadRates(); loadHistory(); };
  useEffect(() => { loadAll(); }, []);

  const openAdd = () => { setForm({ name: "", start: "", end: "" }); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ name: s.name, start: s.start, end: s.end }); setEditId(s.id); setOpen(true); };

  const saveShift = async () => {
    if (!form.name.trim()) { toast.error("Shift name required"); return; }
    if (editId) await api.put(`/shifts/${editId}`, form);
    else await api.post("/shifts", form);
    toast.success("Shift saved");
    setOpen(false); loadAll();
  };
  const removeShift = async (id) => {
    await api.delete(`/shifts/${id}`);
    toast.success("Shift removed");
    loadAll();
  };

  const setRate = (g, sid, v) => setRates({ ...rates, [g]: { ...rates[g], [sid]: v } });

  const saveRates = async () => {
    await api.post("/wage-rates", { effective_date: effectiveDate, rates });
    toast.success(`Rates saved, effective ${effectiveDate}`);
    loadHistory();
  };

  return (
    <div className="p-4 md:p-8 max-w-[1000px] mx-auto w-full space-y-6">
      <PageHeader title="Shifts & Wages" subtitle="Manage your shifts and the wage each gender earns per shift." />

      {/* Shifts */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-800">Shifts</h2>
          <Button data-testid="btn-add-shift" size="sm" onClick={openAdd} className="bg-slate-900 gap-1.5"><Plus size={14} weight="bold" /> Add Shift</Button>
        </div>
        <div className="divide-y divide-slate-100">
          {shifts.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-5 py-3" data-testid={`shift-row-${s.id}`}>
              <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 rounded border font-data text-xs font-semibold ${maps.style[s.id]}`}>{maps.label[s.id]}</span>
                <div>
                  <div className="font-medium text-slate-800">{s.name}</div>
                  <div className="text-xs text-slate-400 font-data">{s.start || "—"} to {s.end || "—"}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`edit-shift-${s.id}`} aria-label="Edit shift" onClick={() => openEdit(s)} className="h-8 w-8 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"><PencilSimple size={16} /></button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-shift-${s.id}`} aria-label="Delete shift" className="h-8 w-8 flex items-center justify-center rounded-md text-rose-500 hover:bg-rose-50"><Trash size={16} /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove {s.name}?</AlertDialogTitle>
                      <AlertDialogDescription>Past attendance already recorded under this shift stays intact. It just won't be selectable for new entries.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => removeShift(s.id)}>Remove</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Wage matrix */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-800">Wage Rates (₹ per day)</h2>
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Effective from</Label>
              <Input data-testid="effective-date" type="date" className="w-40 font-data" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
            </div>
            <Button data-testid="btn-save-rates" onClick={saveRates} className="bg-slate-900 gap-2"><FloppyDisk size={16} weight="bold" /> Save Rates</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-2 font-semibold">Gender</th>
                {shifts.map((s) => <th key={s.id} className="text-center px-4 py-2 font-semibold">{maps.label[s.id]} · {s.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {[["M", "Male"], ["F", "Female"]].map(([g, gl]) => (
                <tr key={g} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium text-slate-700">{gl}</td>
                  {shifts.map((s) => (
                    <td key={s.id} className="px-4 py-3 text-center">
                      <div className="relative w-28 mx-auto">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-data text-sm">₹</span>
                        <Input data-testid={`rate-${g}-${s.id}`} type="number" className="pl-6 font-data text-right"
                          value={rates[g]?.[s.id] ?? 0} onChange={(e) => setRate(g, s.id, e.target.value)} />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Arecanut Seasons */}
      <LabourSeasons />

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 text-xs font-semibold uppercase tracking-widest text-slate-500">Rate History</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-data">
              <thead>
                <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-sans">
                  <th className="text-left px-4 py-2 font-semibold">Effective</th>
                  {["M", "F"].map((g) => shifts.map((s) => <th key={g + s.id} className="text-right px-3 py-2 font-semibold">{g}·{maps.label[s.id]}</th>))}
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.effective_date} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-left">{h.effective_date === "2000-01-01" ? "Default" : h.effective_date}</td>
                    {["M", "F"].map((g) => shifts.map((s) => <td key={g + s.id} className="px-3 py-2 text-right">{h.rates[g]?.[s.id] != null ? fmtMoney(h.rates[g][s.id]) : "—"}</td>))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>{editId ? "Edit Shift" : "Add Shift"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Shift name</Label>
              <Input data-testid="shift-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Night Shift" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Start time</Label>
                <Input data-testid="shift-start" type="time" className="font-data" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>End time</Label>
                <Input data-testid="shift-end" type="time" className="font-data" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
              </div>
            </div>
            <p className="text-xs text-slate-500">New shifts start at ₹0 — set their wage in the table after saving.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="btn-save-shift" className="bg-slate-900" onClick={saveShift}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* =============== Arecanut Seasons =============== */
// Configures the date ranges during which attendance defaults to the
// "Arecanut" balance instead of "Regular" (see Attendance page). This is
// only ever a default -- any single day can still be marked the other way.
function LabourSeasons() {
  const [seasons, setSeasons] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", start_date: todayStr(), note: "" });
  const [closeId, setCloseId] = useState(null);
  const [closeDate, setCloseDate] = useState(todayStr());
  const [reportId, setReportId] = useState(null);

  const load = () => api.get("/labour-seasons").then((r) => setSeasons(r.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm({ name: "", start_date: todayStr(), note: "" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Season name required"); return; }
    if (!form.start_date) { toast.error("Start date required"); return; }
    try {
      await api.post("/labour-seasons", form);
      toast.success("Season saved");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/labour-seasons/${id}`);
    toast.success("Season removed");
    load();
  };

  const saveClose = async () => {
    await api.put(`/labour-seasons/${closeId}`, { end_date: closeDate });
    toast.success("Season closed");
    setCloseId(null); load();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
        <div>
          <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Plant size={18} weight="bold" className="text-emerald-600" /> Arecanut Seasons
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Attendance marked inside a season's dates defaults to the Arecanut balance. You can still override any single day.</p>
        </div>
        <Button data-testid="btn-add-season" size="sm" onClick={openAdd} className="bg-slate-900 gap-1.5"><Plus size={14} weight="bold" /> Add Season</Button>
      </div>
      <div className="divide-y divide-slate-100">
        {seasons.map((s) => (
          <div key={s.id} className="flex items-center justify-between px-5 py-3" data-testid={`season-row-${s.id}`}>
            <div>
              <div className="font-medium text-slate-800 flex items-center gap-2">
                {s.name}
                {s.is_current && <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">Current</span>}
              </div>
              <div className="text-xs text-slate-400 font-data">{s.start_date} → {s.end_date || "ongoing"}</div>
            </div>
            <div className="flex items-center gap-1">
              {!s.end_date && (
                <button data-testid={`close-season-${s.id}`} onClick={() => { setCloseId(s.id); setCloseDate(todayStr()); }}
                  className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1 rounded hover:bg-slate-100">Close season</button>
              )}
              <button data-testid={`view-season-report-${s.id}`} aria-label="Season report" onClick={() => setReportId(s.id)}
                className="h-8 w-8 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"><FileText size={16} /></button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid={`delete-season-${s.id}`} aria-label="Delete season" className="h-8 w-8 flex items-center justify-center rounded-md text-rose-500 hover:bg-rose-50"><Trash size={16} /></button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove {s.name}?</AlertDialogTitle>
                    <AlertDialogDescription>Attendance already marked in this date range keeps whatever bucket it was saved with. This only removes the season bookmark used to suggest a default for new entries.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => remove(s.id)}>Remove</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        {seasons.length === 0 && (
          <div className="px-5 py-8 text-center text-sm text-slate-500">No arecanut seasons configured. Attendance defaults to Regular until you add one.</div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Add Arecanut Season</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Season name</Label>
              <Input data-testid="season-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Arecanut 2026" />
            </div>
            <div className="space-y-1.5">
              <Label>Start date</Label>
              <Input data-testid="season-start" type="date" className="font-data" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Input data-testid="season-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <p className="text-xs text-slate-500">The season stays open until you close it (or add a new one, which auto-closes this one the day before it starts).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="btn-save-season" className="bg-slate-900" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!closeId} onOpenChange={(v) => !v && setCloseId(null)}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader><DialogTitle>Close Season</DialogTitle></DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Last day of the season</Label>
            <Input data-testid="season-close-date" type="date" className="font-data" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} />
            <p className="text-xs text-slate-500">Attendance after this date will default back to Regular (you can still mark any day Arecanut manually).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseId(null)}>Cancel</Button>
            <Button data-testid="btn-confirm-close-season" className="bg-slate-900" onClick={saveClose}>Close Season</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SeasonReport seasonId={reportId} onClose={() => setReportId(null)} />
    </div>
  );
}

function SeasonReport({ seasonId, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!seasonId) { setData(null); return; }
    api.get(`/labour-seasons/${seasonId}/summary`).then((r) => setData(r.data));
  }, [seasonId]);

  const shareText = () => {
    if (!data) return "";
    return `*Arecanut Season Report — ${data.season.name}*\n${data.window_start} to ${data.window_end}\n` +
      `${data.total_labourers} labourer(s) · Earned ${fmtMoney(data.total_earned)} · Balance ${fmtMoney(data.total_balance)}\n\n📎 Full report attached as PDF`;
  };

  const exportExcel = () => {
    if (!data) return;
    const safe = data.season.name.replace(/\s+/g, "_");
    downloadFile(`/labour-seasons/${seasonId}/summary/excel`, `arecanut_season_${safe}.xlsx`);
  };

  return (
    <PrintableStatement open={!!seasonId && !!data} onClose={onClose} testid="labour-season-report" shareText={shareText}
      onExcel={exportExcel} excelLabel="Export Excel"
      pdfFileName={data ? `arecanut_season_${data.season.name.replace(/\s+/g, "_")}.pdf` : undefined}>
      {data && (
        <div className="font-data text-slate-800">
          <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-5">
            <div>
              <h1 className="font-sans text-2xl font-semibold tracking-tight">Arecanut Season Report</h1>
              <p className="text-sm text-slate-500 mt-0.5">{data.season.name}</p>
            </div>
            <div className="text-right text-xs text-slate-500">Period<br /><span className="text-slate-800">{data.window_start} → {data.window_end}</span></div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5 text-center">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Labourers</div>
              <div className="text-lg font-semibold">{data.total_labourers}</div>
            </div>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Active Days</div>
              <div className="text-lg font-semibold">{data.total_days_worked}</div>
            </div>
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Earned</div>
              <div className="text-lg font-semibold text-emerald-700">{fmtMoney(data.total_earned)}</div>
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Advances</div>
              <div className="text-lg font-semibold text-amber-700">{fmtMoney(data.total_advance)}</div>
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Lends</div>
              <div className="text-lg font-semibold text-amber-700">{fmtMoney(data.total_lend)}</div>
            </div>
            <div className="rounded-md bg-slate-900 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Net Balance</div>
              <div className={`text-lg font-semibold ${data.total_balance < 0 ? "text-rose-400" : "text-emerald-400"}`}>{fmtMoney(data.total_balance)}</div>
            </div>
          </div>

          <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-slate-500 mb-2">Per-Labourer Breakdown</h2>
          <table className="w-full text-xs border border-slate-200">
            <thead>
              <tr className="bg-slate-100 text-slate-500 font-sans">
                <th className="px-2 py-1.5 text-left font-semibold">Labourer</th>
                <th className="px-2 py-1.5 text-right font-semibold">Days</th>
                <th className="px-2 py-1.5 text-right font-semibold">Earned</th>
                <th className="px-2 py-1.5 text-right font-semibold">Advance</th>
                <th className="px-2 py-1.5 text-right font-semibold">Lend</th>
                <th className="px-2 py-1.5 text-right font-semibold">Paid</th>
                <th className="px-2 py-1.5 text-right font-semibold">Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.employee_id} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 text-left">{r.employee_name}{!r.employee_active && <span className="text-slate-400"> (removed)</span>}</td>
                  <td className="px-2 py-1.5 text-right">{r.days_worked}</td>
                  <td className="px-2 py-1.5 text-right">{fmtMoney(r.total_earned)}</td>
                  <td className="px-2 py-1.5 text-right">{fmtMoney(r.total_advance)}</td>
                  <td className="px-2 py-1.5 text-right">{fmtMoney(r.total_lend)}</td>
                  <td className="px-2 py-1.5 text-right">{fmtMoney(r.total_paid)}</td>
                  <td className={`px-2 py-1.5 text-right font-semibold ${balanceClass(r.balance)}`}>{fmtMoney(r.balance)}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan={7} className="px-2 py-6 text-center text-slate-400">No arecanut-season activity recorded in this season yet.</td></tr>
              )}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-400 mt-8 font-sans">Generated by AgriLedger · {new Date().toLocaleString("en-IN")}</p>
        </div>
      )}
    </PrintableStatement>
  );
}
